/**
 * Phase 23 — payloadVersion / payloadUpdatedAt in Dexie meta (kein business_*_data KV-Blob).
 */

import { normalizeBusinessStorageId } from '../../utils/sharedCore';
import { getElkheirDb } from './elkheirDb';
import { isEntityStoreAuthorityEnabled } from './entityAggregateStore';
import { isEntityStoreSkipBlobFieldsEnabled } from './businessPayloadAssembly';
import { isPhase23NoKvBlobRuntimeEnabled } from './storagePhase23Meta';

export const BUSINESS_PAYLOAD_META_PREFIX = 'Elkheir:v1:meta:business_payload_v1:';

/** Erfordert AUTHORITY + SKIP_BLOB + (Env oder Runtime-Meta) NO_KV_BLOB */
export function isEntityStoreNoKvBlobEnabled() {
  if (!isEntityStoreAuthorityEnabled() || !isEntityStoreSkipBlobFieldsEnabled()) {
    return false;
  }
  return isPhase23NoKvBlobRuntimeEnabled();
}

/**
 * @param {string} businessId
 */
export function businessPayloadMetaKey(businessId) {
  const n = normalizeBusinessStorageId(businessId);
  if (!n) return null;
  return `${BUSINESS_PAYLOAD_META_PREFIX}${n}`;
}

/**
 * @param {string} businessId
 */
export async function readBusinessPayloadMeta(businessId) {
  const key = businessPayloadMetaKey(businessId);
  if (!key) {
    return { payloadVersion: 0, payloadUpdatedAt: null, noKvBlob: false };
  }

  const db = getElkheirDb();
  if (!db.isOpen()) {
    await db.open();
  }

  const row = await db.meta.get(key);
  const value = row?.value && typeof row.value === 'object' ? row.value : null;
  if (!value) {
    return { payloadVersion: 0, payloadUpdatedAt: null, noKvBlob: false };
  }

  const version = Number(value.payloadVersion);
  return {
    payloadVersion: Number.isFinite(version) && version >= 0 ? version : 0,
    payloadUpdatedAt: value.payloadUpdatedAt || null,
    noKvBlob: value.noKvBlob === true
  };
}

/**
 * @param {string} businessId
 * @param {{ payloadVersion: number, payloadUpdatedAt: string, noKvBlob?: boolean }} meta
 * @param {import('dexie').Dexie} [db]
 */
export async function writeBusinessPayloadMeta(businessId, meta, db = null) {
  const key = businessPayloadMetaKey(businessId);
  if (!key) return false;

  const database = db || getElkheirDb();
  if (!database.isOpen()) {
    await database.open();
  }

  const version = Number(meta.payloadVersion);
  await database.meta.put({
    key,
    value: {
      payloadVersion: Number.isFinite(version) && version >= 0 ? version : 1,
      payloadUpdatedAt: meta.payloadUpdatedAt || new Date().toISOString(),
      noKvBlob: meta.noKvBlob !== false
    },
    updatedAt: Date.now()
  });
  return true;
}

/**
 * @param {string} businessId
 */
export async function reloadFreshBusinessPayloadMetaFromIdb(businessId) {
  return readBusinessPayloadMeta(businessId);
}

/**
 * Phase 24 — IDB-Bereitschaft ohne business_*_data KV-Blob.
 * @param {string} [businessId]
 */
export async function verifyEntityOnlyStorageReadiness(businessId = null) {
  const { getCurrentBusinessId } = await import('../../utils/sharedCore');
  const { countEntityStoresByType } = await import('./entityAggregateStore');

  const result = {
    ready: false,
    noKvBlob: isEntityStoreNoKvBlobEnabled(),
    idbOpen: false,
    payloadMeta: null,
    entityCounts: {},
    reasons: []
  };

  if (!result.noKvBlob) {
    result.reasons.push('no_kv_blob_disabled');
    return result;
  }

  const bid = businessId || getCurrentBusinessId({ allowFallback: false });
  if (!bid) {
    result.reasons.push('no_business');
    return result;
  }

  try {
    const db = getElkheirDb();
    await db.open();
    result.idbOpen = true;

    const meta = await readBusinessPayloadMeta(bid);
    result.payloadMeta = meta;
    if (!meta.noKvBlob) {
      result.reasons.push('payload_meta_not_migrated');
    }

    result.entityCounts = await countEntityStoresByType(bid);
    const totalEntities = Object.values(result.entityCounts || {}).reduce(
      (sum, n) => sum + Number(n || 0),
      0
    );
    const { getEntityBusinessInfo } = await import('./entityAggregateStore');
    const businessInfo = await getEntityBusinessInfo(bid);
    const hasBusinessInfo =
      businessInfo && typeof businessInfo === 'object' && Object.keys(businessInfo).length > 0;

    if (totalEntities === 0 && !hasBusinessInfo && meta.payloadVersion <= 0) {
      result.reasons.push('entity_stores_empty');
    }

    result.ready =
      result.idbOpen &&
      meta.noKvBlob &&
      meta.payloadVersion > 0 &&
      (totalEntities > 0 || hasBusinessInfo);
  } catch (e) {
    result.reasons.push(e?.message || 'entity_only_check_failed');
  }

  return result;
}
